import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from "@angular/core";
import { StudentApiService } from "@agent-ds/shared/services";
import {
  AvailabilityRange,
  AvailabilityRow,
  StudentAvailabilityPeriod
} from "@agent-ds/shared/interfaces";
import * as moment from "moment";
import { Subscription } from "rxjs";

@Component({
  selector: "ag-student-availability-tab",
  templateUrl: "./student-availability-tab.component.html",
  styleUrls: ["./student-availability-tab.component.scss"]
})
export class StudentAvailabilityTabComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() studentId: number;
  @Input() readonly = false;

  minDate: string;
  periods: AvailabilityRow[] = [];
  private refreshSubscription: Subscription | null = null;

  constructor(private readonly studentApiService: StudentApiService) {}

  ngOnInit(): void {
    this.minDate = moment()
      .utcOffset(9)
      .add(1, "days")
      .format("YYYY-MM-DD");

    this.refreshSubscription = this.studentApiService.refreshEvent.subscribe(
      () => {
        if (this.studentId != null) {
          this.load();
        }
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.studentId != null &&
      changes["studentId"] &&
      changes["studentId"].currentValue !== changes["studentId"].previousValue
    ) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

  addCandidate(): void {
    this.periods.push({ date: "", from: "", to: "" });
  }

  removeCandidate(index: number): void {
    this.periods.splice(index, 1);
  }

  save(): void {
    const hasErrors = this.periods.some(
      (period, index) => this.getRowErrors(period, index).length > 0
    );
    if (hasErrors) {
      alert("入力内容に誤りがあります。エラーメッセージを確認してください。");
      return;
    }

    const payload: StudentAvailabilityPeriod[] = this.periods.map(period => {
      const trimmed = this.getTrimmedValues(period);
      const range = this.getRangeFromTrimmed(trimmed);
      if (!range) {
        throw new Error("予期せぬエラー: 不正な候補日が含まれています。");
      }

      return {
        startAt: range.start.toISOString(),
        endAt: range.end.toISOString()
      };
    });

    this.studentApiService
      .updateAvailabilityPeriods(this.studentId, payload)
      .subscribe(() => {
        this.load();
      });
  }

  get showTable(): boolean {
    return this.periods.length > 0;
  }

  // 行のエラーを取得
  getRowErrors(period: AvailabilityRow, index: number): string[] {
    if (!this.hasFullValues(period)) {
      return ["日時・開始・終了時刻をすべて入力してください。"];
    }

    const trimmed = this.getTrimmedValues(period);
    const errors: string[] = [];

    const entryDate = moment(trimmed.date, "YYYY-MM-DD", true)
      .utcOffset(9)
      .startOf("day");
    const today = moment()
      .utcOffset(9)
      .startOf("day");
    if (!entryDate.isValid() || entryDate.isBefore(today)) {
      errors.push("過去の日付は入力できません。");
    }

    const range = this.getRangeFromTrimmed(trimmed);
    if (!range) {
      errors.push("日時の形式が正しくありません。");
      return errors;
    }

    if (!range.start.isBefore(range.end)) {
      errors.push("終了時刻は開始時刻より後にしてください。");
    }

    if (this.hasOverlap(index, range)) {
      errors.push("他の候補日と時間が重複しています。");
    }

    if (
      !this.isMinuteMultipleOfFive(trimmed.from) ||
      !this.isMinuteMultipleOfFive(trimmed.to)
    ) {
      errors.push("分は5分刻みで入力してください。");
    }

    return errors;
  }

  // データを読み込み
  private load(): void {
    this.studentApiService
      .getAvailabilityPeriods(this.studentId)
      .subscribe(response => {
        this.periods = (response.periods || []).map(period => ({
          date: moment(period.startAt)
            .utcOffset(9)
            .format("YYYY-MM-DD"),
          from: moment(period.startAt)
            .utcOffset(9)
            .format("HH:mm"),
          to: moment(period.endAt)
            .utcOffset(9)
            .format("HH:mm")
        }));
      });
  }

  // トリム済みの値を取得
  private getTrimmedValues(period: AvailabilityRow): AvailabilityRow {
    return {
      date: (period.date || "").trim(),
      from: (period.from || "").trim(),
      to: (period.to || "").trim()
    };
  }

  // トリム済みの値から日時範囲を取得
  private getRangeFromTrimmed(
    period: AvailabilityRow
  ): AvailabilityRange | null {
    const start = moment(
      `${period.date} ${period.from}`,
      "YYYY-MM-DD HH:mm",
      true
    );
    const end = moment(`${period.date} ${period.to}`, "YYYY-MM-DD HH:mm", true);

    if (!start.isValid() || !end.isValid()) {
      return null;
    }

    return { start, end };
  }

  // 他の候補日と重複しているかどうかを確認
  private hasOverlap(index: number, currentRange: AvailabilityRange): boolean {
    return this.periods.some((other, otherIndex) => {
      if (otherIndex === index || !this.hasFullValues(other)) {
        return false;
      }

      const trimmedOther = this.getTrimmedValues(other);
      const otherRange = this.getRangeFromTrimmed(trimmedOther);
      if (!otherRange || !otherRange.start.isBefore(otherRange.end)) {
        return false;
      }

      return (
        currentRange.start.isBefore(otherRange.end) &&
        otherRange.start.isBefore(currentRange.end)
      );
    });
  }

  // すべての値が入力されているかどうかを確認
  private hasFullValues(period: AvailabilityRow): boolean {
    const trimmed = this.getTrimmedValues(period);
    return Boolean(trimmed.date && trimmed.from && trimmed.to);
  }

  // 5分刻みかどうかを確認
  private isMinuteMultipleOfFive(value: string): boolean {
    const match = /^\d{1,2}:(\d{2})$/.exec(value);
    if (!match) {
      return true;
    }

    const minute = Number(match[1]);
    return Number.isFinite(minute) && minute % 5 === 0;
  }
}
